import { ParcelData } from "../interfaces/ParcelData";
import { AxiosInstance, AxiosResponse } from "axios";
import HttpClient from "./httpClient";
import { FertilizerTask } from "../interfaces/FertilizerTask";
import { TillageTask } from "../interfaces/TillageTask";
import { HarvestTask } from "../interfaces/HarvestTask";
import { UsageTask } from "../interfaces/UsageTask";
import { SeedTask } from "../interfaces/SeedTask";
import { PlantProtectionTask } from "../interfaces/PlantProtectionTask";
import { ParcelCycle } from "../interfaces/ParcelCycle";

class ParcelService {

    private baseUrl = process.env.REACT_APP_API_BASE_URL as string
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = new HttpClient(this.baseUrl).getAxiosInstance();
    }

    private normalizeDates<T extends Record<string, any>>(obj: T): T {
        const cloned: any = { ...obj };
        for (const key of Object.keys(cloned)) {
            const val = cloned[key];
            if (val instanceof Date) {
                const y = val.getFullYear();
                const m = String(val.getMonth() + 1).padStart(2, "0");
                const d = String(val.getDate()).padStart(2, "0");
                cloned[key] = `${y}-${m}-${d}T00:00:00`;
            }
        }
        return cloned as T;
    }

    async createParcel(parcel: ParcelData): Promise<void> {
        const endpoint = "/parcels";
        const response: AxiosResponse = await this.axiosInstance.post(endpoint, this.normalizeDates(parcel));
        console.debug("Parcel created successfully:", response.data);
    }

    async getParcelByFarmId(id: number): Promise<ParcelData[]> {
        const endpoint = `/farms/${id}/parcels`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("Parcels fetched successfully:", response.data);
        return response.data.data;
    }

    async getParcelById(id: number): Promise<ParcelData> {
        const endpoint = `/parcels/${id}`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("Parcel fetched successfully:", response.data);
        return response.data;
    }

    // Fertilizer
    async getFertalizerTasks(parcelId: number, cycleId: number): Promise<FertilizerTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/fertilizer-tasks`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("Fertilizer tasks fetched successfully:", response.data);
        return response.data.data;
    }

    async createFertilizerTask(parcelId: number, cycleId: number, task: FertilizerTask): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/fertilizer-tasks`;
        const response: AxiosResponse = await this.axiosInstance.post(endpoint, this.normalizeDates(task));
        console.debug("Fertilizer task created successfully:", response.data);
    }

    async updateFertilizerTask(parcelId: number, cycleId: number, taskId: number, task: FertilizerTask): Promise<FertilizerTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/fertilizer-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.patch(endpoint, this.normalizeDates(task));
        console.debug("Fertilizer task updated successfully:", response.data);
        return response.data.data;
    }

    async deleteFertilizerTasks(parcelId: number, cycleId: number, taskId: number): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/fertilizer-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.delete(endpoint);
        console.debug("Fertilizer tasks deleted successfully:", response.data);
    }

    // Tillage
    async getTillageTasks(parcelId: number, cycleId: number): Promise<TillageTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/tillage-tasks`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("Tillage tasks fetched successfully:", response.data);
        return response.data.data;
    }

    async createTillageTask(parcelId: number, cycleId: number, task: TillageTask): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/tillage-tasks`;
        const response: AxiosResponse = await this.axiosInstance.post(endpoint, this.normalizeDates(task));
        console.debug("Tillage task created successfully:", response.data);
    }

    async updateTillageTask(parcelId: number, cycleId: number, taskId: number, task: TillageTask): Promise<TillageTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/tillage-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.patch(endpoint, this.normalizeDates(task));
        console.debug("Tillage task updated successfully:", response.data);
        return response.data.data;
    }

    async deleteTillageTask(parcelId: number, cycleId: number, taskId: number): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/tillage-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.delete(endpoint);
        console.debug("Tillage tasks deleted successfully:", response.data);
    }

    // Harvest
    async getHarvestTasks(parcelId: number, cycleId: number): Promise<HarvestTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/harvest-tasks`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("Harvest tasks fetched successfully:", response.data);
        return response.data.data;
    }

    async createHarvestTask(parcelId: number, cycleId: number, task: HarvestTask): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/harvest-tasks`;
        const response: AxiosResponse = await this.axiosInstance.post(endpoint, this.normalizeDates(task));
        console.debug("Harvest task created successfully:", response.data);
    }

    async updateHarvestTask(parcelId: number, cycleId: number, taskId: number, task: HarvestTask): Promise<HarvestTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/harvest-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.patch(endpoint, this.normalizeDates(task));
        console.debug("Harvest task updated successfully:", response.data);
        return response.data.data;
    }

    async deleteHarvestTask(parcelId: number, cycleId: number, taskId: number): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/harvest-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.delete(endpoint);
        console.debug("Harvest tasks deleted successfully:", response.data);
    }

    // Usage
    async getUsageTasks(parcelId: number, cycleId: number): Promise<UsageTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/usage-tasks`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("Usage tasks fetched successfully:", response.data);
        return response.data.data;
    }

    async createUsageTask(parcelId: number, cycleId: number, task: UsageTask): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/usage-tasks`;
        const response: AxiosResponse = await this.axiosInstance.post(endpoint, this.normalizeDates(task));
        console.debug("Usage task created successfully:", response.data);
    }

    async updateUsageTask(parcelId: number, cycleId: number, taskId: number, task: UsageTask): Promise<UsageTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/usage-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.patch(endpoint, this.normalizeDates(task));
        console.debug("Usage task updated successfully:", response.data);
        return response.data.data;
    }

    async deleteUsageTask(parcelId: number, cycleId: number, taskId: number): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/usage-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.delete(endpoint);
        console.debug("Usage tasks deleted successfully:", response.data);
    }

    // Seed
    async getSeedTasks(parcelId: number, cycleId: number): Promise<SeedTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/seed-tasks`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("Seed tasks fetched successfully:", response.data);
        return response.data.data;
    }

    async createSeedTask(parcelId: number, cycleId: number, task: SeedTask): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/seed-tasks`;
        const response: AxiosResponse = await this.axiosInstance.post(endpoint, this.normalizeDates(task));
        console.debug("Seed task created successfully:", response.data);
    }

    async updateSeedTask(parcelId: number, cycleId: number, taskId: number, task: SeedTask): Promise<SeedTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/seed-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.patch(endpoint, this.normalizeDates(task));
        console.debug("Seed task updated successfully:", response.data);
        return response.data.data;
    }

    async deleteSeedTask(parcelId: number, cycleId: number, taskId: number): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/seed-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.delete(endpoint);
        console.debug("Seed tasks deleted successfully:", response.data);
    }

    // Plant protection
    async getPlantProtectionTasks(parcelId: number, cycleId: number): Promise<PlantProtectionTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/plant-protection-tasks`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("plant-protection tasks fetched successfully:", response.data);
        return response.data.data;
    }

    async createPlantProtectionTask(parcelId: number, cycleId: number, task: PlantProtectionTask): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/plant-protection-tasks`;
        const response: AxiosResponse = await this.axiosInstance.post(endpoint, this.normalizeDates(task));
        console.debug("plant-protection task created successfully:", response.data);
    }

    async updatePlantProtectionTask(parcelId: number, cycleId: number, taskId: number, task: PlantProtectionTask): Promise<PlantProtectionTask[]> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/plant-protection-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.patch(endpoint, this.normalizeDates(task));
        console.debug("Plant-protection task updated successfully:", response.data);
        return response.data.data;
    }

    async deletePlantProtectionTask(parcelId: number, cycleId: number, taskId: number): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/plant-protection-tasks/${taskId}`;
        const response: AxiosResponse = await this.axiosInstance.delete(endpoint);
        console.debug("plant-protection tasks deleted successfully:", response.data);
    }

    // Parcel cycles and report
    async getParcelCycle(id: number): Promise<ParcelCycle[]> {
        const endpoint = `/parcels/${id}/cycles`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint);
        console.debug("cycles tasks fetched successfully:", response.data);
        return response.data.data;
    }

    async getCurrentCycleForParcel(id: number): Promise<ParcelCycle> {
        const cycles = await this.getParcelCycle(id);
        if (!cycles || cycles.length === 0) {
            console.warn("No cycles found for parcel:", id);
            return this.createCycle(id, { startDate: new Date(), completed: false })
        }
        const activeCycles = cycles.filter(cycle => !cycle.completed);
        if (activeCycles.length === 0) {
            console.warn("No active cycles found for parcel:", id);
            throw new Error();
        }
        const latestCycle = activeCycles.reduce((latest, cycle) => {
            return new Date(cycle.startDate) > new Date(latest.startDate) ? cycle : latest;
        });
        return latestCycle;
    }

    async editCycleById(parcelId: number, cycleId: number, cycle: ParcelCycle): Promise<void> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}`;
        const response: AxiosResponse = await this.axiosInstance.patch(endpoint, this.normalizeDates(cycle));
        console.debug("cycle tasks update successfully:", response.data);
    }

    async createCycle(parcelId: number, cycle: ParcelCycle): Promise<ParcelCycle> {
        const endpoint = `/parcels/${parcelId}/cycles`;
        const response: AxiosResponse = await this.axiosInstance.post(endpoint, this.normalizeDates(cycle));
        console.debug("cycle created successfully:", response.data);
        return response.data;
    }

    async exportCycle(parcelId: number, cycleId: number) : Promise<any> {
        const endpoint = `/parcels/${parcelId}/cycles/${cycleId}/report`;
        const response: AxiosResponse = await this.axiosInstance.get(endpoint, {
            responseType: "blob",
        });
        console.debug("report created successfully:", response.data);
        return response;
    }

    // Parcel delete/update
    async deleteById(parcelId: number): Promise<void> {
        const endpoint = `/parcels/${parcelId}`;
        const response: AxiosResponse = await this.axiosInstance.delete(endpoint);
        console.debug("parcels tasks deleted successfully:", response.data);
    }

    async editById(parcelId: string, parcelData: ParcelData): Promise<void> {
        const endpoint = `/parcels/${parcelId}`;
        const response: AxiosResponse = await this.axiosInstance.patch(endpoint, this.normalizeDates(parcelData));
        console.debug("parcels tasks update successfully:", response.data);
    }
}

export default ParcelService;
